/**
 * Reference Source: react-beautiful-dnd example
 * https://codesandbox.io/s/zqwz5n5p9x?file=/src/index.js
 */
import React, { useEffect, useState, memo } from 'react';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

export type SortableListPropsType<T> = {
  list: T[];
  droppableId: string;
  onDragEndHandler: (result: DropResult, sortedList?: T[]) => void;
}

type DefaultSortListType = {
  id: number;
  name: string;
  tag?: string;
}

type DraggableItemType<T> = {
  listItem: T;
  index: number;
}

type ItemListType<T> = {
  items: T[];
}

const reorder = <T extends DefaultSortListType>(
  list: T[], startIndex: number, endIndex: number,
): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const DraggableItem = <T extends DefaultSortListType>({
  listItem,
  index,
}: DraggableItemType<T>): JSX.Element => (
  <Draggable draggableId={listItem.id.toString()} index={index}>
    {(provided) => (
      <Box
        ref={provided.innerRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
        {...provided.draggableProps}
          // eslint-disable-next-line react/jsx-props-no-spreading
        {...provided.dragHandleProps}
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        <ListItem>
          <ListItemText primary={listItem.name} />
          <ListItemSecondaryAction>
            {
              listItem.tag && (
                <Chip label={listItem.tag} />
              )
            }
          </ListItemSecondaryAction>
        </ListItem>
        <Divider />
      </Box>
    )}
  </Draggable>
  );

const ItemList = <T extends DefaultSortListType>({
  items,
}: ItemListType<T>) => (
  <>
    {
      items.map((li, index) => (
        <DraggableItem<T>
          key={li.id}
          listItem={li}
          index={index}
        />
      ))
    }
  </>
  );

const typedMemo: <T>(c: T) => T = memo;
const MemoizedItemList = typedMemo(ItemList);

const SortableList = <T extends DefaultSortListType>({
  list,
  droppableId,
  onDragEndHandler,
}: SortableListPropsType<T>): JSX.Element => {
  const [stateList, setStateList] = useState<T[]>(list);
  // update stateList on Add or delete
  useEffect(() => {
    if (list.length !== stateList.length) {
      setStateList(list);
    }
  }, [list, stateList.length]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = reorder(
      stateList,
      result.source.index,
      result.destination.index,
    );
    setStateList(items);
    onDragEndHandler(result, items);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <List
            ref={provided.innerRef}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.droppableProps}
          >
            <>
              <MemoizedItemList
                items={stateList}
              />
              {provided.placeholder}
            </>
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default SortableList;
